// src/services/subscriptionRestrictionsService.js
import { supabase } from '../config';
// import { fetchUserRoles } from './subscriptionApi'; // Removed unused import

/**
 * Service for checking subscription-based restrictions per product
 */
class SubscriptionRestrictionsService {
  /**
   * Get user's current subscription plan
   */
  async getUserPlan(userId) {
    try {
      // Get user's subscription from database (include 'trial' status)
      const { data: subscription, error } = await supabase
        .from('subscriptions')
        .select('plan_type, status')
        .eq('user_id', userId)
        .in('status', ['active', 'trial']) // Include both active and trial statuses
        .maybeSingle(); // Use maybeSingle instead of single to avoid errors

      // If no subscription found or any error, default to free
      if (error || !subscription) {
        // Only log if it's a non-404 error (table doesn't exist, RLS issue, etc.)
        if (error && error.code !== 'PGRST116' && error.code !== 'PGRST301') {
          // Silently handle subscription query errors - this is non-critical
          // console.warn('Subscription query failed (non-critical):', error.message);
        }
        return 'free';
      }

      return subscription?.plan_type || 'free';
    } catch (error) {
      // Silently handle errors - subscription checks are non-critical
      return 'free';
    }
  }

  /**
   * Get plan restrictions for a specific product
   */
  async getPlanRestrictions(planType, productName) {
    try {
      // Get plan details from database
      const { data: plan, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('plan_type', planType)
        .single();

      if (error) {
        console.error('Error fetching plan restrictions:', error);
        // Return default restrictions
        return this.getDefaultRestrictions(planType, productName);
      }

      // Parse product restrictions from plan
      const restrictions = plan.product_restrictions || {};
      return restrictions[productName] || this.getDefaultRestrictions(planType, productName);
    } catch (error) {
      console.error('Error getting plan restrictions:', error);
      return this.getDefaultRestrictions(planType, productName);
    }
  }

  /**
   * Get default restrictions based on plan type
   */
  getDefaultRestrictions(planType, productName) {
    // Map trial plan to basic restrictions (trial gets same features as basic)
    const effectivePlanType = planType === 'trial' ? 'basic' : planType;
    
    const defaults = {
      free: {
        equity: { max_companies: 1, max_shareholders: 5, enabled: true },
        dataroom: { max_projects: 3, enabled: true },
        questions: { max_questions: 10, enabled: true },
        capraise: { max_deals: 0, enabled: false },
        analysis: { data_access: 'limited', enabled: true }
      },
      basic: {
        equity: { max_companies: 2, max_shareholders: 20, enabled: true },
        dataroom: { max_projects: 10, enabled: true },
        questions: { max_questions: 50, enabled: true },
        capraise: { max_deals: 3, enabled: true },
        analysis: { data_access: 'standard', enabled: true }
      },
      pro: {
        equity: { max_companies: 5, max_shareholders: 50, enabled: true },
        dataroom: { max_projects: 20, enabled: true },
        questions: { max_questions: -1, enabled: true }, // -1 = unlimited
        capraise: { max_deals: 5, enabled: true },
        analysis: { data_access: 'extended', enabled: true }
      }
    };

    return defaults[effectivePlanType]?.[productName] || { enabled: false };
  }

  /**
   * Check if user can perform an action for a product
   */
  async canUserAccess(userId, productName, action = 'access') {
    try {
      const planType = await this.getUserPlan(userId);
      const restrictions = await this.getPlanRestrictions(planType, productName);

      // Check if product is enabled for this plan
      if (!restrictions.enabled) {
        return {
          allowed: false,
          reason: `${productName} is not available on the ${planType} plan`,
          upgradeRequired: true
        };
      }

      return {
        allowed: true,
        restrictions,
        planType
      };
    } catch (error) {
      console.error('Error checking user access:', error);
      return {
        allowed: false,
        reason: 'Error checking access permissions'
      };
    }
  }

  /**
   * Check if user can create more items (companies, projects, etc.)
   */
  async canCreateMore(userId, productName, currentCount) {
    try {
      const planType = await this.getUserPlan(userId);
      const restrictions = await this.getPlanRestrictions(planType, productName);

      if (!restrictions.enabled) {
        return {
          allowed: false,
          reason: `${productName} is not available on the ${planType} plan`,
          upgradeRequired: true
        };
      }

      // Determine the limit field based on product
      const limitField = this.getLimitField(productName);
      const maxAllowed = restrictions[limitField];

      // -1 means unlimited
      if (maxAllowed === -1) {
        return {
          allowed: true,
          remaining: -1,
          restrictions
        };
      }

      const remaining = maxAllowed - currentCount;
      const allowed = remaining > 0;

      return {
        allowed,
        remaining: Math.max(0, remaining),
        limit: maxAllowed,
        current: currentCount,
        reason: allowed 
          ? `You can create ${remaining} more` 
          : `You've reached the limit of ${maxAllowed} for the ${planType} plan`,
        upgradeRequired: !allowed,
        restrictions
      };
    } catch (error) {
      console.error('Error checking create limit:', error);
      return {
        allowed: false,
        reason: 'Error checking limits'
      };
    }
  }

  /**
   * Get the limit field name for a product
   */
  getLimitField(productName) {
    const fieldMap = {
      equity: 'max_companies',
      dataroom: 'max_projects',
      questions: 'max_questions',
      capraise: 'max_deals',
      investor_crm: 'max_contacts',
      marketData: 'data_access'
    };
    return fieldMap[productName] || 'max_items';
  }

  /**
   * Get all restrictions for a user
   */
  async getUserRestrictions(userId) {
    try {
      const planType = await this.getUserPlan(userId);
      const products = ['equity', 'dataroom', 'questions', 'capraise', 'investor_crm', 'marketData'];
      
      const restrictions = {};
      for (const product of products) {
        restrictions[product] = await this.getPlanRestrictions(planType, product);
      }

      return {
        planType,
        restrictions
      };
    } catch (error) {
      console.error('Error getting user restrictions:', error);
      return {
        planType: 'free',
        restrictions: {}
      };
    }
  }
}

// Export singleton instance
const subscriptionRestrictionsService = new SubscriptionRestrictionsService();
export default subscriptionRestrictionsService;

